import { FileFormat } from './types'

type FileFormatSpec = {
  format: FileFormat
  mimeType: string
  extension: string
}

export const FILE_FORMATS: Record<FileFormat, FileFormatSpec> = {
  PDF: { format: 'PDF', mimeType: 'application/pdf', extension: 'pdf' },
  JPEG: { format: 'JPEG', mimeType: 'image/jpeg', extension: 'jpeg' },
  PNG: { format: 'PNG', mimeType: 'image/png', extension: 'png' },
  DOC: { format: 'DOC', mimeType: 'application/msword', extension: 'doc' },
  DOCX: {
    format: 'DOCX',
    mimeType:
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    extension: 'docx',
  },
  XLS: {
    format: 'XLS',
    mimeType: 'application/vnd.ms-excel',
    extension: 'xls',
  },
  XLSX: {
    format: 'XLSX',
    mimeType:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: 'xlsx',
  },
  DWG: {
    format: 'DWG',
    // Non-image MIME so CAD files route to download handling rather than
    // matching `type.includes('image')` checks in attachment previews.
    mimeType: 'application/acad',
    extension: 'dwg',
  },
  DXF: { format: 'DXF', mimeType: 'application/dxf', extension: 'dxf' },
  MP4: { format: 'MP4', mimeType: 'video/mp4', extension: 'mp4' },
}

// Reverse lookups, built once from FILE_FORMATS so they can never drift from it.
const FORMAT_BY_MIME: Record<string, FileFormat> = {}
const FORMAT_BY_EXTENSION: Record<string, FileFormat> = {}
for (const spec of Object.values(FILE_FORMATS)) {
  FORMAT_BY_MIME[spec.mimeType] = spec.format
  FORMAT_BY_EXTENSION[spec.extension] = spec.format
}

// Common aliases some platforms report (e.g. JPEG as `image/jpg` / `.jpg`).
FORMAT_BY_MIME['image/jpg'] = 'JPEG'
FORMAT_BY_EXTENSION.jpg = 'JPEG'

// CAD has no single registered MIME — browsers/OSes report several. We emit the
// canonical `application/acad` / `application/dxf` (see FILE_FORMATS), but accept
// every common alias on input so detection doesn't fall back to the extension.
FORMAT_BY_MIME['image/vnd.dwg'] = 'DWG'
FORMAT_BY_MIME['application/x-dwg'] = 'DWG'
FORMAT_BY_MIME['image/vnd.dxf'] = 'DXF'
FORMAT_BY_MIME['application/x-dxf'] = 'DXF'

/**
 * Resolve a file's `FileFormat` from its MIME type, falling back to the file
 * extension. Returns `null` for unsupported types so callers can reject them.
 */
export const computeFileFormat = (
  mimeType?: string | null,
  fileName?: string | null,
): FileFormat | null => {
  const normalisedMime = mimeType?.toLowerCase().split(';')[0].trim()
  if (normalisedMime && FORMAT_BY_MIME[normalisedMime]) {
    return FORMAT_BY_MIME[normalisedMime]
  }

  const extension = fileName?.split('.').pop()?.toLowerCase()
  if (extension && FORMAT_BY_EXTENSION[extension]) {
    return FORMAT_BY_EXTENSION[extension]
  }

  return null
}
